'use client';

import { useState } from 'react';
import { Loader2, CheckCircle2, Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { messagesApi } from '@/lib/api';

type Step = 'idle' | 'submitting' | 'success' | 'error';

export function ContactForm() {
    const [step, setStep] = useState<Step>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStep('submitting');
        setErrorMsg('');

        try {
            await messagesApi.sendGeneralContact({
                name,
                email,
                phone: phone || undefined,
                subject: subject || undefined,
                body,
            });
            setStep('success');
            setName('');
            setEmail('');
            setPhone('');
            setSubject('');
            setBody('');
        } catch (err: unknown) {
            const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
            const msg = e?.response?.data?.message ?? e?.message ?? 'Une erreur est survenue. Veuillez réessayer.';
            setErrorMsg(Array.isArray(msg) ? msg.join(' ') : msg);
            setStep('error');
        }
    };

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
                <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center ring-8 ring-success/5">
                    <CheckCircle2 className="h-7 w-7 text-success" />
                </div>
                <div>
                    <p className="font-display text-h4 text-ink">Message envoyé.</p>
                    <p className="text-sm text-ink-secondary mt-2 max-w-sm">
                        Merci de nous avoir contactés. Notre équipe vous répondra dans les
                        plus brefs délais.
                    </p>
                </div>
                <button
                    onClick={() => setStep('idle')}
                    className="link-underline text-sm text-accent mt-2"
                >
                    Envoyer un autre message
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink">
                        Nom complet <span className="text-error">*</span>
                    </label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Prénom et nom"
                        required
                        autoComplete="name"
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink">
                        E-mail <span className="text-error">*</span>
                    </label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        required
                        autoComplete="email"
                    />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink">
                        Téléphone <span className="text-ink-muted font-normal">(optionnel)</span>
                    </label>
                    <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+212 6XX XXX XXX"
                        autoComplete="tel"
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-ink">
                        Objet <span className="text-ink-muted font-normal">(optionnel)</span>
                    </label>
                    <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Demande d'information"
                        maxLength={200}
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink">
                    Message <span className="text-error">*</span>
                </label>
                <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Décrivez votre demande en détail…"
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={6}
                />
                <p className="text-right text-xs text-ink-muted">{body.length}/2000</p>
            </div>

            {step === 'error' && errorMsg && (
                <div
                    role="alert"
                    className="flex items-start gap-2.5 rounded-xl border border-error/30 bg-error/5 px-4 py-3 text-sm text-error"
                >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{errorMsg}</span>
                </div>
            )}

            <Button
                type="submit"
                variant="navy"
                size="lg"
                disabled={step === 'submitting'}
                className="w-full gap-2"
            >
                {step === 'submitting' ? (
                    <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Envoi en cours…
                    </>
                ) : (
                    <>
                        <Send className="h-4 w-4" />
                        Envoyer le message
                    </>
                )}
            </Button>

            <p className="text-xs text-ink-muted text-center">
                En soumettant ce formulaire, vous acceptez que vos coordonnées soient
                utilisées pour vous répondre.
            </p>
        </form>
    );
}
